<template>
	<div
		v-if="dropdownReady"
		:id="`dropdown-group-${_uid}`"
		v-click-outside="onClickOutside"
		data-cy="dropdown-group"
	>
		<div
			v-if="label && showLabel"
			data-cy="dropdown-group-label"
			class="dropdown__heading"
		>
			<label
				class="dropdown__label"
				:for="`dropdown-group-${_uid}`"
			>
				{{ label }}

				<helio-information
					v-if="infoMessage"
					:message="infoMessage"
					:gap="infoMessageGap"
					:color="infoColor"
					:icon="infoIcon"
					:icon-weight="infoIconWeight"
					:skin="infoMessageSkin"
				/>
			</label>
		</div>

		<div
			:class="[
				{
					'is-opened': opened,
					'is-disabled': disabled,
					'is-filled': !showPlaceholder,
					'is-search-results': searchedTerm,
				},
				`dropdown--${size}`
			]"
			:aria-label="ariaLabel"
			data-cy="dropdown-group-parent-wrapper"
			class="dropdown__wrapper"
		>
			<Trap :disabled="!showTrap">
				<div
					class="dropdown dropdown-group"
					data-cy="dropdown-group-wrapper"
					:tabindex="disabled ? '-1' : '0'"
					:aria-label="label"
					@click="toggle"
					@keyup.space="toggle"
					@keyup.enter="toggle"
				>
					<span
						v-if="showPlaceholder"
						class="dropdown__placeholder"
					>
						<helio-icon
							v-if="iconToken"
							:token="iconToken"
							:weight="iconWeight"
							class="dropdown__placeholder-icon"
						/>

						{{ placeholder }}
					</span>

					<span
						v-if="showNormalValue"
						class="dropdown__value"
						data-cy="dropdown-group-value"
					>
						<helio-icon
							v-if="iconToken"
							:token="iconToken"
							:weight="iconWeight"
							class="dropdown__placeholder-icon"
						/>

						{{ dropdownDisplayText }}
					</span>

					<span
						v-if="showAvatarValue"
						class="dropdown__value dropdown__value--avatar"
					>
						<helio-avatar
							size="xx-small"
							:user="{
								image: selected[0].image
							}"
						/>

						{{ selected[0].label }}
					</span>

					<span
						v-if="searchActive"
						class="dropdown__search"
						data-cy="dropdown-group-search"
					>
						<helio-field
							:placeholder="$t('dropdown.search')"
							:autofocus="searchFocus"
							:show-validators="false"
							icon="search"
							icon-weight="light"
							icon-position="left"
							@changed="search"
						/>
					</span>

					<helio-icon
						class="dropdown__caret"
						token="angle-down"
						weight="regular"
					/>
				</div>

				<div
					ref="list-wrapper"
					class="dropdown__list-wrapper"
					aria-label="dropdown selector"
					role="dialog"
				>
					<div
						v-show="isDropdownListShown"
						ref="list"
						:class="{
							'scroll': isScrollable,
							'dropdown__list--with-reset': reset,
						}"
						class="dropdown__list"
						data-cy="dropdown-group-list"
						:tabindex="disabled ? '-1' : tabIndex"
						role="listbox"
						:aria-hidden="!opened ? 'true' : null"
						:aria-label="$t('aria-label.dropdown')"
					>
						<template v-if="multiple && selectByGroup">
							<ul
								v-for="(group, index) in groupList"
								:key="index"
								class="dropdown__list-grouped"
								role="group"
								:aria-label="group ? group : $t('dropdown.group-other')"
							>
								<li
									v-for="(option, dataIndex) in groupedData[index]"
									:ref="option.selectable ? 'selectables' : null"
									:key="dataIndex"
									:class="{
										'dropdown__item--multiple' : multiple,
										'dropdown__item--section-title' : option.role === 'presentation',
										'is-selected' : isSelected(option) && !option.disabled,
										'is-disabled': option.disabled,
									}"
									class="dropdown__item dropdown-group__item--grouped"
									data-cy="dropdown-group-item"
									:data-label="option.label || option.text"
									:role="option.role"
									:aria-hidden="option.disabled"
									:aria-selected="isSelected(option)"
									:tabindex="option.disabled || !opened ? -1 : 0"
									@click="option.disabled ? '' : selectOption(option)"
									@keyup.enter="option.disabled ? '' : selectOption(option)"
									@keyup.space="option.disabled ? '' : selectOption(option)"
								>
									<template>
										<helio-icon
											v-if="option.label && itemIconToken"
											:token="itemIconToken"
											:weight="itemIconWeight"
											class="dropdown__icon"
											:class="`dropdown__icon--${itemIconPosition}`"
											@keydown.enter.stop="$emit('icon-click', option)"
											@keydown.space.stop="$emit('icon-click', option)"
											@click.stop="$emit('icon-click', option)"
										/>

										<helio-checkbox
											:initial-checked="isSelected(option)"
											:label="option.label || option.text"
											tabindex="-1"
										/>
									</template>
								</li>
							</ul>
						</template>

						<template v-else>
							<ul
								v-for="(group, index) in groupList"
								:key="index"
								class="dropdown__list-grouped"
								role="group"
								:aria-label="group ? group : $t('dropdown.group-other')"
							>
								<li
									v-for="(option, dataIndex) in groupedData[index]"
									:ref="option.selectable ? 'selectables' : null"
									:key="dataIndex"
									:class="{
										'is-disabled': option.disabled,
										'is-selected' : isSelected(option) && !multiple && !option.disabled,
										'dropdown__item--icon' : layout === 'icon',
										'dropdown__item--avatar' : layout === 'avatar',
										'dropdown__item--multiple' : multiple,
										'dropdown__item--section-title' : option.role === 'presentation',
									}"
									class="dropdown__item dropdown-group__item--grouped"
									data-cy="dropdown-group-item"
									:data-label="option.label || option.text"
									:role="option.role"
									:aria-selected="isSelected(option)"
									:tabindex="option.disabled ? -1 : option.role === 'presentation' ? -1 : tabIndex"
									@click="option.disabled ? '' : option.label ? selectOption(option) : ''"
									@keyup.enter="option.disabled ? '' : option.label ? selectOption(option) : ''"
									@keyup.space="option.disabled ? '' : option.label ? selectOption(option) : ''"
								>
									<template
										v-if="(layout === 'normal' && !multiple)
											|| option.role === 'presentation'"
									>
										<helio-icon
											v-if="option.label && itemIconToken"
											:token="itemIconToken"
											:weight="itemIconWeight"
											class="dropdown__icon"
											:class="`dropdown__icon--${itemIconPosition}`"
											@keydown.enter.stop="$emit('icon-click', option)"
											@keydown.space.stop="$emit('icon-click', option)"
											@click.stop="$emit('icon-click', option)"
										/>

										<span
											class="lowlight"
											v-html="option.label
												? highlightTerms(option.label, searchedTerm)
												: option.text"
										/>
									</template>

									<template v-if="multiple">
										<helio-checkbox
											:initial-checked="isSelected(option)"
											:label="option.label"
											tabindex="-1"
										/>
									</template>

									<template v-if="layout === 'avatar' && option.role !== 'presentation'">
										<helio-avatar
											:image="option.image"
											:user="{
												image: option.image
											}"
											size="xx-small"
										/>

										{{ option.label }}
									</template>
								</li>
							</ul>
						</template>

						<ul
							v-if="groupedData.length === 0"
							class="dropdown__list"
						>
							<li class="dropdown__item dropdown__item--empty">
								{{ $t('dropdown.no-results') }}
							</li>
						</ul>
					</div>

					<div
						v-if="reset"
						class="dropdown__reset"
						data-cy="dropdown-reset"
						@keyup.enter="clear"
						@keyup.space="clear"
						@click="clear"
					>
						<helio-icon
							token="redo"
							weight="regular"
						/>

						{{ resetText ? resetText : $t('dropdown.reset') }}
					</div>
				</div>
			</Trap>
		</div>

		<div
			v-if="hint"
			class="dropdown__hint"
		>
			{{ hint }}
		</div>

		<div
			v-if="showTags"
			class="dropdown__tags"
		>
			<ul
				class="tags__list"
				data-cy="tags-list"
			>
				<li
					v-for="(item, index) in selectedOptions"
					:key="index"
					:class="{
						'tag__item--delete': !disabled,
					}"
					class="tag__item"
					data-cy="tags-item"
				>
					{{ item.label }}

					<helio-icon
						v-if="!disabled"
						class="tags__delete"
						token="times"
						weight="light"
						data-cy="tags-item-delete"
						@keydown.enter.stop="unSelectOption(item)"
						@keydown.space.stop="unSelectOption(item)"
						@click.stop="unSelectOption(item)"
					/>
				</li>
			</ul>
		</div>

		<div
			v-if="errors.length > 0"
			class="dropdown__error"
		>
			<helio-errors :items="errors" />
		</div>
	</div>
</template>

<script>
import _groupBy from 'lodash/groupBy';
import _forEach from 'lodash/forEach';
import _mapValues from 'lodash/mapValues';
import _findIndex from 'lodash/findIndex';
import Trap from 'vue-focus-lock';
import Avatar from '../avatar/Avatar.vue';
import Checkbox from '../checkbox/Checkbox.vue';
import Errors from '../errors/Errors.vue';
import Field from '../field/Field.vue';
import Icon from '../icon/Icon.vue';
import Information from '../information/Information.vue';

export default {
	name: 'DropdownGroup',

	components: {
		Trap,
		'helio-avatar': Avatar,
		'helio-checkbox': Checkbox,
		'helio-errors': Errors,
		'helio-field': Field,
		'helio-icon': Icon,
		'helio-information': Information,
	},

	props: {
		closeOnSelect: {
			type: Boolean,
			required: false,
			default: false,
			description: 'Sets whether the Dropdown closes when a selection is made',
		},

		disabled: {
			type: Boolean,
			required: false,
			default: false,
			description: 'Sets whether the Dropdown will be disabled for the user',
		},

		hint: {
			type: String,
			required: false,
			default: null,
			description: 'Sets a hint that will appear below the unopened Dropdown',
		},

		iconToken: {
			type: String,
			required: false,
			default: null,
			description: 'Sets the icon that will appear to the left of the selected item when the Dropdown is in its closed state',
		},

		iconWeight: {
			type: String,
			required: false,
			default: 'regular',
			validator: value => [
				'bold',
				'brand',
				'light',
				'regular',
			].indexOf(value) !== -1,
			description: 'Sets the weight of the iconToken',
		},

		infoColor: {
			type: String,
			required: false,
			default: null,
			description: 'Sets the color of the infoIcon. The colour provided must be one of those in the $colors-list.',
		},

		infoIcon: {
			type: String,
			required: false,
			default: 'question-circle',
			description: 'Sets the icon that will appear next to the Dropdown\'s label. Clicking the icon will open a tooltip.',
		},

		infoIconWeight: {
			type: String,
			required: false,
			default: 'bold',
			description: 'Sets the weight of the icon that will appear next to the Dropdown\'s label',
		},

		infoMessage: {
			type: String,
			required: false,
			default: null,
			description: 'Sets the message that will appear in the infoIcon\'s tooltip',
		},

		infoMessageGap: {
			type: Number,
			required: false,
			default: 12,
			description: 'Sets the space in px between the anchor and the point of the Tooltip',
		},

		infoMessageSkin: {
			type: String,
			required: false,
			default: 'light',
			validator: value => [
				'dark',
				'light',
			].indexOf(value) !== -1,
			description: 'Sets whether the Tooltip will have a dark or light skin',
		},

		initialErrors: {
			type: Array,
			required: false,
			default: () => [],
			description: 'Sets the text that will appear below the Dropdown',
		},

		initialFocus: {
			type: String,
			required: false,
			default: '',
			description: 'Sets which item will be focused on by default',
		},

		initialSelected: {
			type: [Array, Object],
			required: false,
			default: null,
			description: 'Sets which items will already be selected by default',
		},

		itemIconPosition: {
			type: String,
			required: false,
			default: 'right',
			validator: value => [
				'left',
				'right',
			].indexOf(value) !== -1,
			description: 'Sets the horizontal alignment of the itemIcon',
		},

		itemIconToken: {
			type: String,
			required: false,
			default: null,
			description: 'Sets whether there will be an icon in the list item and which icon it will be',
		},

		itemIconWeight: {
			type: String,
			required: false,
			default: 'regular',
			validator: value => [
				'bold',
				'brand',
				'light',
				'regular',
			].indexOf(value) !== -1,
			description: 'Sets the weight of the itemIcon',
		},

		label: {
			type: String,
			required: false,
			default: null,
			description: 'Sets the text of the Dropdown\'s label',
		},

		layout: {
			type: String,
			required: false,
			default: 'normal',
			validator: value => [
				'avatar',
				'icon',
				'normal',
			].indexOf(value) !== -1,
			description: 'Sets the layout of the list item (e.g., "avatar")',
		},

		multiple: {
			type: Boolean,
			required: false,
			default: false,
			description: 'Sets whether multiple items can be selected',
		},

		options: {
			type: Array,
			required: true,
			description: 'Supplies information about each list item to the Dropdown component',
		},

		placeholder: {
			type: String,
			required: false,
			default: '',
			description: 'Sets the placeholder text that will appear in the input field',
		},

		reset: {
			type: Boolean,
			required: false,
			default: false,
			description: 'Sets whether there will be a reset button in the Dropdown',
		},

		resetText: {
			type: String,
			required: false,
			default: null,
			description: 'Sets the text that will appear in the reset button',
		},

		searchable: {
			type: Boolean,
			required: false,
			default: false,
			description: 'Sets whether the user can enter text into an input field to search list items',
		},

		selectByGroup: {
			type: Boolean,
			required: false,
			default: false,
			description: 'Sets whether the user can select entire groups in multiple selection mode',
		},

		showLabel: {
			type: Boolean,
			required: false,
			default: true,
			description: 'Set whether we show the label or not',
		},

		showTags: {
			type: Boolean,
			required: false,
			default: false,
			description: 'Sets whether tags will be shown below the closed Dropdown component',
		},

		size: {
			type: String,
			required: false,
			default: 'normal',
			validator: value => [
				'normal',
				'large',
			].indexOf(value) !== -1,
			description: 'Sets the height of the Dropdown component',
		},
	},

	data() {
		return {
			closeable: !this.searchActive,
			dropdownReady: false,
			errors: [],
			indexOfCurrentSelection: -1,
			isDropdownListShown: false,
			isScrollable: false,
			lastFocusedElement: null,
			opened: false,
			searchActive: false,
			searchedTerm: '',
			selected: [],
			showTrap: false,
			tabIndex: '-1',
		};
	},

	computed: {
		ariaLabel() {
			return this.label && !this.showLabel ? this.label : null;
		},

		groupList() {
			return Object.keys(this.groupedFilteredOptions);
		},

		groupedData() {
			const data = [];

			this.groupList.forEach((group) => {
				const options = this.groupedFilteredOptions[group];

				// get group title
				const groupTitle = {
					role: 'presentation',
					id: group,
					text: group || this.$t('dropdown.group-other'),
					selectable: this.selectByGroup,
				};

				// get group options
				const groupOptions = [];
				options.forEach(option => {
					groupOptions.push({
						...option,
						role: 'option',
						selectable: !option.disabled,
					});
				});

				// assemble each title with associated options
				if (groupOptions.length) {
					data.push([groupTitle, ...groupOptions]);
				}
			});

			return data;
		},

		dropdownDisplayText() {
			const arr = this.selectedOptions.filter(item => !item.disabled);

			if (arr.length) {
				const name = arr[0].label;

				if (arr.length > 1) {
					return `${name} (+ ${arr.length - 1})`;
				}

				return name;
			}

			return '';
		},

		selectedOptions() {
			return this.selected.filter(item => !!item.label);
		},

		cleanedOptions() {
			return this.options.map((item) => {
				const clone = { ...item };

				// falsey groups should all be converted to empty string
				if (!clone.group && clone.group !== '') {
					clone.group = '';
				}

				return clone;
			});
		},

		filteredOptions() {
			const searchFor = this.searchedTerm.toLowerCase();

			return !searchFor ? this.cleanedOptions : this.cleanedOptions.filter((option) => {
				if (option.label && !option.label.toLowerCase().match(searchFor)) {
					return false;
				}

				return true;
			});
		},

		groupedOptions() {
			return _groupBy(this.cleanedOptions, 'group');
		},

		groupedFilteredOptions() {
			return _groupBy(this.filteredOptions, 'group');
		},

		groupedSelectedAllStates() {
			return _mapValues(this.groupedOptions,
				arr => arr.filter(this.isOptionSelected).length === arr.length);
		},

		showAvatarValue() {
			return this.selected.length > 0 && this.layout === 'avatar' && !this.searchActive;
		},

		showNormalValue() {
			return this.selected.length > 0 && !this.searchActive && this.layout !== 'avatar' && !this.showTags;
		},

		showPlaceholder() {
			return (this.selected.length === 0 && !this.searchActive) || this.showTags;
		},
	},

	watch: {
		groupedData() {
			this.dropdownReady = true;
		},

		initialErrors(newErrors) {
			this.errors = newErrors;
		},

		initialSelected: {
			handler(newVal) {
				this.selected = [];
				this.assignInitialSelectedValues(newVal);
			},
			immediate: true,
		},

		selected(newSelection) {
			this.$emit('changed', [...newSelection]);
		},

		groupedSelectedAllStates: {
			handler(newVal) {
				if (!this.selectByGroup) {
					return;
				}

				// @TODO ça loop sur toutes à chaque fois, j'aimerais mieux checker juste ceux qui changent
				_forEach(newVal, (value, id) => {
					const selected = this.isGroupSelected({ id });

					if (value === true && !selected) {
						// @TODO j'aimerais mieux passer une ref à un objet existant ici
						this.selectOption({ id, role: 'presentation' });
					} else if (value === false && selected) {
						this.unSelectOption({ id });
					}
				});
			},
			immediate: true,
		},

		opened(newVal) {
			if (this.closingListWrapperTimeout) {
				clearTimeout(this.closingListWrapperTimeout);
			}

			if (newVal) {
				// shown instantly during open animation
				this.isDropdownListShown = true;
			} else {
				// wait for close animation to finish before hiding
				// NOTE: the delay must match the CSS transition time
				this.closingListWrapperTimeout = setTimeout(() => {
					this.isDropdownListShown = false;
				}, 200);
			}
		},
	},

	mounted() {
		window.addEventListener('keyup', this.checkForEscape);
		window.addEventListener('keyup', this.checkForArrows);

		if (this.groupedData.length) {
			this.dropdownReady = true;
		}

		if (this.initialErrors) {
			this.errors = this.initialErrors;
		}

		window.addEventListener('resize', this.onResize);
		setTimeout(() => {
			this.onResize();
		}, 1000);
	},

	beforeDestroy() {
		window.removeEventListener('keyup', this.checkForEscape);
		window.removeEventListener('keyup', this.checkForArrows);
		window.removeEventListener('resize', this.onResize);
	},

	methods: {
		// * Opening and closing methods
		toggle(e) {
			if (this.disabled) {
				return;
			}

			let tag = null;

			if (typeof e !== 'undefined') {
				tag = e.target.tagName;
			}

			if (this.opened && tag !== 'INPUT') {
				this.close();
			} else {
				this.open();

				if (this.searchable) {
					this.searchActive = true;
					this.searchFocus = true;
				}
			}
		},

		open() {
			this.opened = true;
			this.tabIndex = '0';
			this.lastFocusedElement = document.activeElement;
			this.showTrap = true;
			this.$emit('open');

			if (!!this.initialFocus && this.$refs.list) {
				const selector = `.dropdown-group__item[data-label="${this.initialFocus}"]`;
				const dropdownList = this.$refs.list;
				const dropdownItem = dropdownList.querySelector(selector);

				if (dropdownItem) {
					const { offsetTop, clientHeight } = dropdownItem;
					dropdownList.scrollTo(0, (offsetTop - clientHeight));
				}
			}
		},

		close() {
			if (this.searchable) {
				this.searchActive = false;
				this.searchFocus = false;
				this.searchedTerm = '';
			}

			this.showTrap = false;
			this.tabIndex = '-1';
			setTimeout(() => {
				if (this.lastFocusedElement) {
					this.lastFocusedElement.focus();
				}
			}, 100);
			this.opened = false;

			this.$emit('close');
		},

		onClickOutside() {
			if (this.opened) {
				this.close();
			}
		},

		// * Selection methods
		assignInitialSelectedValues(values = []) {
			let finalValue = this.selected;

			if (values && values.length > 0) {
				finalValue = values;
			} else if (values && !(values instanceof Array)) {
				finalValue.push(values);
			}

			finalValue = finalValue.map(item => {
				if (!item.group && !item.disabled) {
					return {
						...item,
						group: 'Other',
					};
				}
				return item;
			});

			this.$set(this, 'selected', finalValue);
		},

		unSelectOption(option) {
			const searchFor = this.isGroup(option) ? { id: option.id } : { label: option.label };
			const index = _findIndex(this.selected, searchFor);

			if (index !== -1) {
				this.remove(index);
			}
		},

		selectOption(option) {
			this.toggleOption(option);

			if (this.isGroup(option) && this.multiple && this.selectByGroup) {
				if (this.isGroupSelected(option)) {
					this.selectAllGroupItems(option);
				} else {
					this.unSelectAllGroupItems(option);
				}
			}

			// update aussi le indexOfCurrentSelection quand on sélectionne un item lors d'un click
			// sinon la navigation clavier ne sera pas au même endroit que le focus du browser
			this.updateCurrentSelectionIndexFromSelectedOption(option);
		},

		toggleOption(option) {
			const searchFor = this.isGroup(option) ? { id: option.id } : { label: option.label };
			const index = _findIndex(this.selected, searchFor);
			const selected = index !== -1;

			if (this.multiple && !selected) {
				this.selected.push(option);
			} else if (this.multiple && selected) {
				this.remove(index);
			} else {
				this.selected.splice(0, 1, option);
			}

			if (this.closeOnSelect) {
				this.searchActive = false;
				this.toggle();
			}
		},

		updateCurrentSelectionIndexFromSelectedOption(option) {
			const flatGroupedData = this.groupedData.flat().filter(item => item.selectable);
			const index = flatGroupedData.indexOf(option);

			if (index !== -1) {
				this.indexOfCurrentSelection = index;
			}
		},

		isSelected(option) {
			if (this.selectByGroup) {
				const selected = option.label
					? this.isOptionSelected(option)
					: this.isGroupSelected(option);

				const inactiveOption = option.disabled;

				return inactiveOption ? false : selected;
			}

			const selected = this.isOptionSelected(option);
			const sectionTitle = option.role === 'presentation';
			const inactiveOption = option.disabled;

			return (sectionTitle || inactiveOption) ? false : selected;
		},

		isGroup(option) {
			return typeof option.id !== 'undefined';
		},

		isOptionSelected(option) {
			return !!this.selected.find(selection => selection.label === option.label);
		},

		isGroupSelected(option) {
			return !!this.selected.find(selection => selection.id === option.id);
		},

		selectAllGroupItems(option) {
			this.groupedOptions[option.id].forEach((item) => {
				if (!this.isOptionSelected(item)) {
					this.selectOption(item);
				}
			});
		},

		unSelectAllGroupItems(option) {
			this.groupedOptions[option.id].forEach((item) => {
				if (this.isOptionSelected(item)) {
					this.unSelectOption(item);
				}
			});
		},

		remove(index) {
			this.selected.splice(index, 1);
		},

		clear() {
			this.selected = [];
			this.close();
		},

		// * Other methods
		onResize() {
			if (this.$refs.list) {
				const { scrollHeight } = this.$refs.list;
				const threshold = this.reset ? 163 : 200;

				this.isScrollable = (scrollHeight > threshold);
			}
		},

		search(term) {
			this.$emit('search', term);
			this.searchedTerm = term;
		},

		highlightTerms(dropdownOption, searchTerm) {
			if (!searchTerm) {
				return dropdownOption;
			}

			return dropdownOption.replace(new RegExp(searchTerm, 'gi'), (match) => `<span class="highlight">${match}</span>`);
		},

		checkForEscape(event) {
			const { keyCode } = event;

			if (keyCode === 27) {
				this.onClickOutside();
			}
		},

		checkForArrows(event) {
			const { keyCode } = event;

			if (!this.opened || [38, 40].indexOf(keyCode) === -1) {
				return;
			}

			event.preventDefault();

			const listArray = this.$refs.selectables || [];

			// Up arrow
			if (keyCode === 38 && this.indexOfCurrentSelection > 0) {
				this.indexOfCurrentSelection = listArray
					.findLastIndex((item, index) => index < this.indexOfCurrentSelection);
			}
			// Down arrow
			if (keyCode === 40 && this.indexOfCurrentSelection < listArray.length - 1) {
				this.indexOfCurrentSelection = listArray
					.findIndex((item, index) => index > this.indexOfCurrentSelection);
			}

			// focus selected element
			if (typeof listArray[this.indexOfCurrentSelection] !== 'undefined') {
				listArray[this.indexOfCurrentSelection].focus();
			}
		},
	},
};
</script>

<style lang="scss" src="../dropdown/dropdown.scss" />
<style lang="scss" src="./dropdown-group.scss" />
